import { APIStatus } from "@/api/APIStatus.type";
import { getPlayers, getTrendingPlayers } from "@/api/api";
import { getSbcSets } from "@/api/privateRequests/sbcs/getSbcSets";
import {
  type PopularSquad,
  getLeaderboardSquads,
  getOwnSquad,
} from "@/api/publicRequests/leaderboard/getLeaderboardSquads";
import {
  AllTrending,
  getAllTrending,
} from "@/api/publicRequests/trending/getAllTrending";
import {
  TrendingSBC,
  getTrendingSbcs,
} from "@/api/publicRequests/trending/getTrendingSbcs";
import { TrendingContentType } from "@/pages/FrontPage/TrendingOrNewContent/TrendingOrNewContent";
import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  getTrendingEvoPlayers,
  undoVoteSbcSet,
  voteSbcSet,
} from "@repo/shared/api";
import { Platform } from "@repo/shared/redux";
import { EvolutionPlayer, Player } from "@repo/shared/types";

import { IRootState } from "../store";
import { userFavoriteClubIdsSelector } from "../user/userSlice";

export type { PopularSquad };

/*
  The picker is a 2-step guided flow (Club + Country, order reversible) that may
  route through a login gate before persisting. `login` is only reachable from
  `picker`.
*/
export type FavoritesStep = "picker" | "login";
export type FavoriteType = "club" | "country";

/** A single source the filled-section grid can be narrowed to. */
export type FavoriteSource =
  | "all"
  | { type: "club"; id: number }
  | { type: "country"; id: number };

export const MAX_FAVORITE_CLUBS = 3;

type FavoritesFlow = {
  step: FavoritesStep;
  /** Which favorite type the picker is currently editing. */
  pickerType: FavoriteType;
  /** Whichever type was chosen first becomes step 1; the other is optional step 2. */
  firstChosen: FavoriteType | null;
  selectedClubIds: number[];
  selectedCountryId: number | null;
};

type State = {
  currentSquad: PopularSquad | null;
  currentSquadStatus: APIStatus;
  popularSquads: PopularSquad[];
  popularSquadsStatus: APIStatus;
  activePage: number;
  favoritesFlow: FavoritesFlow;
  favoriteClubPlayers: Player[];
  favoriteClubPlayersStatus: APIStatus;
  favoriteCountryPlayers: Player[];
  favoriteCountryPlayersStatus: APIStatus;
  favoritesFeedPlayers: Player[];
  favoritesFeedStatus: APIStatus;
  trendingPlayers: Player[];
  trendingEvoPlayers: EvolutionPlayer[];
  trendingSbcs: TrendingSBC[];
  contentStatus: APIStatus;
  activeTrendingTab: TrendingContentType;
  allTrending: AllTrending[];
  activeFavoriteClubTab: FavoriteClubTab;
  activeNewTab: NewContentType;
  newPlayers: Player[];
  newEvoPlayers: Player[];
  newSbcs: TrendingSBC[];
  newContentStatus: APIStatus;
};

export type FavoriteClubTab = "players" | "evolution-players" | "pnp";

const initialFavoritesFlow: FavoritesFlow = {
  step: "picker",
  pickerType: "club",
  firstChosen: null,
  selectedClubIds: [],
  selectedCountryId: null,
};
const initialState: State = {
  currentSquad: null,
  currentSquadStatus: "idle",
  popularSquads: [],
  popularSquadsStatus: "idle",
  activePage: 0,
  favoritesFlow: initialFavoritesFlow,
  favoriteClubPlayers: [],
  favoriteClubPlayersStatus: "idle",
  favoriteCountryPlayers: [],
  favoriteCountryPlayersStatus: "idle",
  favoritesFeedPlayers: [],
  favoritesFeedStatus: "idle",
  trendingPlayers: [],
  contentStatus: "idle",
  trendingEvoPlayers: [],
  trendingSbcs: [],
  allTrending: [],
  activeTrendingTab: "all",
  activeFavoriteClubTab: "players",
  activeNewTab: "players",
  newPlayers: [],
  newEvoPlayers: [],
  newSbcs: [],
  newContentStatus: "idle",
};

export const fetchPopularSquads = createAsyncThunk(
  "frontPage/fetchPopularSquads",
  async () => {
    return await getLeaderboardSquads({ random: true });
  },
);

export const fetchUserSquad = createAsyncThunk(
  "frontPage/fetchUserSquad",
  async () => {
    return await getOwnSquad();
  },
);

export const fetchFavoriteClubPlayers = createAsyncThunk(
  "frontPage/fetchFavoriteClubPlayers",
  async ({
    clubId,
    activeTab,
  }: {
    clubId: number;
    activeTab: FavoriteClubTab;
  }) => {
    const queryParams: any = [{ clubs: [clubId] }];
    if (activeTab === "pnp") {
      queryParams.push({ pastAndPresentClubs: [clubId] });
    }
    const response = await getPlayers({
      page: 1,
      sort:
        activeTab === "evolution-players"
          ? "evo-popularity"
          : activeTab === "pnp"
            ? "meta-rating"
            : "date-added",
      evolutions: activeTab === "evolution-players",
      queryParams,
    });
    return response.players.slice(0, 16);
  },
);

/** Top players for the favorite-country live preview (mirrors club fetch). */
export const fetchFavoriteCountryPlayers = createAsyncThunk(
  "frontPage/fetchFavoriteCountryPlayers",
  async ({ countryId }: { countryId: number }) => {
    const response = await getPlayers({
      page: 1,
      sort: "meta-rating",
      queryParams: [{ countries: [countryId] }],
    });
    return response.players.slice(0, 16);
  },
);

const sortForTab = (activeTab: FavoriteClubTab) =>
  activeTab === "evolution-players"
    ? ("meta-rating" as const)
    : activeTab === "pnp"
      ? ("meta-rating" as const)
      : ("date-added" as const);

/*
  Merged feed for the filled section. `source === "all"` sends the
  `favorite-clubs-and-countries` flag so the backend OR-matches the signed-in
  user's saved clubs and country in one query; a narrowed source fetches only
  that club/country.
*/
export const fetchFavoritesFeed = createAsyncThunk(
  "frontPage/fetchFavoritesFeed",
  async (
    {
      source,
      activeTab,
      sort: sortOverride,
    }: {
      source: FavoriteSource;
      activeTab: FavoriteClubTab;
      /** The tab's own order unless given, e.g. a sort the user picked. */
      sort?: Parameters<typeof getPlayers>[0]["sort"];
    },
    { getState },
  ) => {
    const sort = sortOverride ?? sortForTab(activeTab);
    const evolutions = activeTab === "evolution-players";

    if (source === "all") {
      /*
        Past & Present is club-only, so the OR flag is wrong here: it would pull
        in the favorite country and surface players who never played for any of
        the favorite clubs. Query the saved club ids instead.
      */
      if (activeTab === "pnp") {
        const clubIds = userFavoriteClubIdsSelector(getState() as IRootState);
        // The tab stays selected after the last favorite club is removed.
        if (clubIds.length === 0) return [];
        const res = await getPlayers({
          page: 1,
          sort,
          queryParams: [{ clubs: clubIds }, { pastAndPresentClubs: clubIds }],
        });
        return res.players.slice(0, 16);
      }

      const res = await getPlayers({
        page: 1,
        sort,
        evolutions,
        favoriteClubsAndCountries: true,
      });
      return res.players.slice(0, 16);
    }

    const queryParams: any =
      source.type === "club"
        ? [{ clubs: [source.id] }]
        : [{ countries: [source.id] }];
    if (source.type === "club" && activeTab === "pnp") {
      queryParams.push({ pastAndPresentClubs: [source.id] });
    }
    const res = await getPlayers({ page: 1, sort, evolutions, queryParams });
    return res.players.slice(0, 16);
  },
);

const convertSbcToTrendingSBC = ({
  sbc,
  platform,
}: {
  sbc: any;
  platform: Platform;
}): TrendingSBC => {
  return {
    ...sbc,
    reward: sbc.rewards?.[0],
    price: platform === "Playstation" ? sbc.psPrice : sbc.pcPrice,
  };
};
export const fetchTrendingContent = createAsyncThunk(
  "frontPage/fetchTrendingContent",
  async ({ type }: { type: TrendingContentType }, { getState }) => {
    const state = getState() as IRootState;
    const platform = state.userReducer.user.platform || "Playstation";
    if (type === "all") {
      const res = await getAllTrending();

      let allTrending: AllTrending[] = [];
      res.forEach((item) => {
        if (item.contentType === "sbc") {
          allTrending.push({
            contentType: "sbc",
            content: convertSbcToTrendingSBC({ sbc: item.content, platform }),
          });
        } else {
          allTrending.push(item);
        }
      });

      return { content: allTrending, type };
    } else if (type === "players") {
      const res = await getTrendingPlayers();
      return { content: res, type };
    } else if (type === "evolutions") {
      const res = await getTrendingEvoPlayers();
      return { content: res, type };
    } else if (type === "sbc") {
      const res = await getTrendingSbcs();
      return {
        content: res.map((sbc) => convertSbcToTrendingSBC({ sbc, platform })),
        type,
      };
    }
    throw new Error("Invalid trending content type");
  },
);

export type NewContentType = "players" | "evolutions" | "sbc";

/*
  "New" feed = newest content per type: players / evo players via getPlayers
  sorted date-added, SBCs via the newest sbc-sets page.
*/
export const fetchNewContent = createAsyncThunk(
  "frontPage/fetchNewContent",
  async ({ type }: { type: NewContentType }, { getState }) => {
    if (type === "players") {
      const res = await getPlayers({ page: 1, sort: "date-added" });
      return { type, content: res.players.slice(0, 16) };
    }
    if (type === "evolutions") {
      const res = await getPlayers({
        page: 1,
        sort: "date-added",
        evolutions: true,
      });
      return { type, content: res.players.slice(0, 16) };
    }
    const state = getState() as IRootState;
    const platform = state.userReducer.user.platform || "Playstation";
    const sets = await getSbcSets();
    return {
      type,
      content: sets
        .slice(0, 8)
        .map((sbc) => convertSbcToTrendingSBC({ sbc, platform })),
    };
  },
);

const frontPageSlice = createSlice({
  name: "frontPage",
  initialState,
  extraReducers: (builder) => {
    builder.addCase(fetchPopularSquads.fulfilled, (state, action) => {
      state.popularSquads = action.payload;

      state.popularSquadsStatus = "fulfilled";
      if (state.activePage >= action.payload.length) {
        state.activePage = 0;
      }
    });
    builder.addCase(fetchPopularSquads.pending, (state) => {
      state.popularSquadsStatus = "pending";
    });
    builder.addCase(fetchPopularSquads.rejected, (state) => {
      state.popularSquadsStatus = "rejected";
    });
    builder.addCase(fetchUserSquad.fulfilled, (state, action) => {
      state.currentSquad = action.payload;
      state.currentSquadStatus = "fulfilled";
    });
    builder.addCase(fetchUserSquad.pending, (state) => {
      state.currentSquadStatus = "pending";
    });
    builder.addCase(fetchUserSquad.rejected, (state) => {
      state.currentSquadStatus = "rejected";
    });
    builder.addCase(fetchFavoriteClubPlayers.fulfilled, (state, action) => {
      state.favoriteClubPlayers = action.payload;
      state.favoriteClubPlayersStatus = "fulfilled";
    });
    builder.addCase(fetchFavoriteClubPlayers.pending, (state) => {
      state.favoriteClubPlayersStatus = "pending";
    });
    builder.addCase(fetchFavoriteClubPlayers.rejected, (state) => {
      state.favoriteClubPlayersStatus = "rejected";
    });
    builder.addCase(fetchFavoriteCountryPlayers.fulfilled, (state, action) => {
      state.favoriteCountryPlayers = action.payload;
      state.favoriteCountryPlayersStatus = "fulfilled";
    });
    builder.addCase(fetchFavoriteCountryPlayers.pending, (state) => {
      state.favoriteCountryPlayersStatus = "pending";
    });
    builder.addCase(fetchFavoriteCountryPlayers.rejected, (state) => {
      state.favoriteCountryPlayersStatus = "rejected";
    });
    builder.addCase(fetchFavoritesFeed.fulfilled, (state, action) => {
      state.favoritesFeedPlayers = action.payload;
      state.favoritesFeedStatus = "fulfilled";
    });
    builder.addCase(fetchFavoritesFeed.pending, (state) => {
      state.favoritesFeedStatus = "pending";
    });
    builder.addCase(fetchFavoritesFeed.rejected, (state) => {
      state.favoritesFeedStatus = "rejected";
    });
    builder.addCase(fetchTrendingContent.fulfilled, (state, action) => {
      if (action.payload.type === "all") {
        state.allTrending = action.payload.content;
      } else if (action.payload.type === "players") {
        state.trendingPlayers = action.payload.content;
      } else if (action.payload.type === "evolutions") {
        state.trendingEvoPlayers = action.payload.content;
      } else if (action.payload.type === "sbc") {
        state.trendingSbcs = action.payload.content;
      }
      state.contentStatus = "fulfilled";
    });
    builder.addCase(fetchTrendingContent.pending, (state) => {
      state.contentStatus = "pending";
    });
    builder.addCase(fetchTrendingContent.rejected, (state) => {
      state.contentStatus = "rejected";
    });
    builder.addCase(fetchNewContent.fulfilled, (state, action) => {
      if (action.payload.type === "players") {
        state.newPlayers = action.payload.content as Player[];
      } else if (action.payload.type === "evolutions") {
        state.newEvoPlayers = action.payload.content as Player[];
      } else if (action.payload.type === "sbc") {
        state.newSbcs = action.payload.content as TrendingSBC[];
      }
      state.newContentStatus = "fulfilled";
    });
    builder.addCase(fetchNewContent.pending, (state) => {
      state.newContentStatus = "pending";
    });
    builder.addCase(fetchNewContent.rejected, (state) => {
      state.newContentStatus = "rejected";
    });
  },
  reducers: {
    /**
     * A vote cast from the front page. The SBCs-page reducer indexes into its
     * own list, which is empty here, so the same optimistic arithmetic is
     * applied to whichever front-page feeds carry the set.
     */
    voteSbc(
      state,
      action: PayloadAction<{ setId: number; vote: "UPVOTE" | "DOWNVOTE" }>,
    ) {
      const { setId, vote } = action.payload;
      const isUpvote = vote === "UPVOTE";

      const sets = [
        ...state.trendingSbcs,
        ...state.newSbcs,
        ...state.allTrending
          .filter((item) => item.contentType === "sbc")
          .map((item) => item.content as TrendingSBC),
      ].filter((sbc) => +sbc.id === setId);

      if (sets.length === 0) return;

      /* A set can sit in more than one feed; the request goes out once. */
      const undo = isUpvote ? sets[0].wasUpvoted : sets[0].wasDownvoted;
      if (undo) undoVoteSbcSet(setId, vote);
      else voteSbcSet(setId, vote);

      sets.forEach((sbc) => {
        if (isUpvote) {
          sbc.upVotes += undo ? -1 : 1;
          if (sbc.wasDownvoted) sbc.downVotes -= 1;
          sbc.wasDownvoted = false;
          sbc.wasUpvoted = !undo;
        } else {
          sbc.downVotes += undo ? -1 : 1;
          if (sbc.wasUpvoted) sbc.upVotes -= 1;
          sbc.wasUpvoted = false;
          sbc.wasDownvoted = !undo;
        }
      });
    },
    setCurrentSquad(state, action: PayloadAction<PopularSquad | null>) {
      state.currentSquad = action.payload;
    },
    setPopularSquads(state, action: PayloadAction<PopularSquad[]>) {
      state.popularSquads = action.payload;
      if (state.activePage >= action.payload.length) {
        state.activePage = 0;
      }
    },
    setActivePage(state, action: PayloadAction<number>) {
      state.activePage = action.payload;
    },
    setFavoritesStep(state, action: PayloadAction<FavoritesStep>) {
      state.favoritesFlow.step = action.payload;
    },
    setPickerType(state, action: PayloadAction<FavoriteType>) {
      state.favoritesFlow.pickerType = action.payload;
    },
    toggleSelectedClub(state, action: PayloadAction<number>) {
      const flow = state.favoritesFlow;
      const id = action.payload;
      if (flow.selectedClubIds.includes(id)) {
        flow.selectedClubIds = flow.selectedClubIds.filter((c) => c !== id);
      } else if (flow.selectedClubIds.length < MAX_FAVORITE_CLUBS) {
        flow.selectedClubIds.push(id);
        if (!flow.firstChosen) flow.firstChosen = "club";
      }
    },
    setSelectedCountry(state, action: PayloadAction<number | null>) {
      const flow = state.favoritesFlow;
      flow.selectedCountryId = action.payload;
      if (action.payload && !flow.firstChosen) flow.firstChosen = "country";
    },
    /** Seed the flow from the user's saved favorites when opening to edit. */
    initFavoritesFlow(
      state,
      action: PayloadAction<{ clubIds: number[]; countryId: number | null }>,
    ) {
      const { clubIds, countryId } = action.payload;
      state.favoritesFlow = {
        ...initialFavoritesFlow,
        selectedClubIds: clubIds.slice(0, MAX_FAVORITE_CLUBS),
        selectedCountryId: countryId,
        firstChosen: clubIds.length ? "club" : countryId ? "country" : null,
      };
    },
    resetFavoritesFlow(state) {
      state.favoritesFlow = initialFavoritesFlow;
    },
    setActiveTrendingTab(state, action: PayloadAction<TrendingContentType>) {
      state.activeTrendingTab = action.payload;
    },
    setActiveFavoriteClubTab(state, action: PayloadAction<FavoriteClubTab>) {
      state.activeFavoriteClubTab = action.payload;
    },
    setActiveNewTab(state, action: PayloadAction<NewContentType>) {
      state.activeNewTab = action.payload;
    },
  },
});

export const frontPageSelector = (state: IRootState) => state.frontPageReducer;
export const currentSquadSelector = (state: IRootState) =>
  state.frontPageReducer.currentSquad;
export const popularSquadsSelector = (state: IRootState) =>
  state.frontPageReducer.popularSquads;
export const activePopularSquadSelector = (state: IRootState) => {
  const { popularSquads, activePage } = state.frontPageReducer;
  return popularSquads[activePage] ?? null;
};
export const activePageSelector = (state: IRootState) =>
  state.frontPageReducer.activePage;
export const favoritesFlowSelector = (state: IRootState) =>
  state.frontPageReducer.favoritesFlow;
export const activeTrendingTabSelector = (state: IRootState) =>
  state.frontPageReducer.activeTrendingTab;
export const trendingSbcsSelector = (state: IRootState) =>
  state.frontPageReducer.trendingSbcs;
export const activeFavoriteClubTabSelector = (state: IRootState) =>
  state.frontPageReducer.activeFavoriteClubTab;
export const activeNewTabSelector = (state: IRootState) =>
  state.frontPageReducer.activeNewTab;

export const {
  setCurrentSquad,
  setPopularSquads,
  setActivePage,
  setFavoritesStep,
  setPickerType,
  toggleSelectedClub,
  setSelectedCountry,
  initFavoritesFlow,
  resetFavoritesFlow,
  setActiveTrendingTab,
  setActiveFavoriteClubTab,
  setActiveNewTab,
  voteSbc,
} = frontPageSlice.actions;
export default frontPageSlice.reducer;
